import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bike, CreditCard, Link2, PackageCheck, X } from 'lucide-react';
import {
  CLAVE_POSICION_AVISO,
  CLAVE_SEGUNDOS_AVISO,
  avisosNuevos,
  clasesDePosicion,
  leerPosicionAviso,
  leerSegundosAviso,
  type PosicionAviso,
  type Aviso,
  type PedidoParaAviso,
  type TipoAviso,
} from '@shared/avisosDomicilio';
import { puedeUsarCanal } from '@shared/permisos';
import { useAjustesPermisos } from '@renderer/store/useAjustesPermisos';
import { useSessionStore } from '@renderer/store/useSessionStore';

/**
 * Sprint 103 — Los globos que avisan lo que pasa a domicilio.
 *
 * Petición de la dueña: *"un globo que aparezca por unos segundos… por si Claudia está en otra
 * sección del sistema"*. Ella está sola: mientras revisa el inventario o cierra el día, un pedido
 * puede entrar por el enlace y quedarse ahí sin que nadie lo sepa.
 *
 * ## Las cuatro decisiones de diseño
 *
 * 1. **No es modal.** No interrumpe ni roba el foco: aparece, se puede tocar, y se va solo. Un
 *    modal a media captura de otro pedido sería peor que el problema que resuelve.
 * 2. **Se toca y lleva al pedido.** Un aviso que informa sin dar salida es una llamada al soporte;
 *    éste abre el tablero en el pedido que lo produjo.
 * 3. **No aparece si ya estás en el tablero.** Ahí los pedidos se ven; duplicarlos en un globo es
 *    ruido, y el ruido es lo que hace que un día se ignore el aviso que sí importaba.
 * 4. **Respeta los permisos.** Sólo lo monta quien puede ver el tablero de domicilios. La cocina no
 *    tiene por qué enterarse de quién transfirió.
 */
export function AvisosDomicilio() {
  const navigate = useNavigate();
  const location = useLocation();
  const sesion = useSessionStore((s) => s.sesion);
  const ajustes = useAjustesPermisos((s) => s.ajustes);
  const puedeVerDomicilios = puedeUsarCanal(
    sesion?.rol,
    'pedidos:tablero-domicilios',
    ajustes,
  );

  const [visibles, setVisibles] = useState<Aviso[]>([]);
  const [segundos, setSegundos] = useState(0);
  /**
   * Sprint 108 — Dónde aparece.
   *
   * Nació abajo a la derecha y el primer reporte fue *"ni lo vi"*: la vista estaba arriba, en los
   * botones grandes. Un aviso fuera del campo visual no es un aviso.
   */
  const [posicion, setPosicion] = useState<PosicionAviso>('arriba-centro');
  /**
   * La foto anterior del tablero. `null` = todavía no hay contra qué comparar, y por eso el primer
   * ciclo no anuncia nada: abrir el sistema con nueve pedidos en curso no son nueve noticias.
   */
  const anterior = useRef<PedidoParaAviso[] | null>(null);
  /** Lo ya anunciado, para que un refresco no repita el mismo globo. */
  const yaAvisado = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!puedeVerDomicilios) return;
    void (async () => {
      const [r, pos] = await Promise.all([
        window.api.config.obtener(CLAVE_SEGUNDOS_AVISO),
        window.api.config.obtener(CLAVE_POSICION_AVISO),
      ]);
      setSegundos(leerSegundosAviso(r.ok ? r.data : null));
      setPosicion(leerPosicionAviso(pos.ok ? pos.data : null));
    })();
  }, [puedeVerDomicilios]);

  const revisar = useCallback(async () => {
    const r = await window.api.pedidos.tableroDomicilios();
    if (!r.ok) return;
    const ahora: PedidoParaAviso[] = r.data.map((p) => ({
      id: p.id,
      numeroPedido: p.numeroPedido,
      motivo: p.motivo,
      clienteNombre: p.clienteNombre,
      avisoPagoCliente: p.avisoPagoCliente ?? null,
      fechaRecibidoCliente: p.fechaRecibidoCliente ?? null,
    }));

    const nuevos = avisosNuevos(anterior.current, ahora).filter(
      (a) => !yaAvisado.current.has(a.clave),
    );
    anterior.current = ahora;
    if (nuevos.length === 0) return;

    for (const a of nuevos) yaAvisado.current.add(a.clave);
    setVisibles((v) => [...v, ...nuevos]);
  }, []);

  useEffect(() => {
    if (!puedeVerDomicilios) return;
    void revisar();
    /*
      Mismo ritmo que el tablero (15 s). Más seguido no aporta —un pedido a domicilio no se
      resuelve en diez segundos— y le costaría batería a una laptop que está abierta todo el día.
    */
    const t = setInterval(() => void revisar(), 15000);
    return () => clearInterval(t);
  }, [puedeVerDomicilios, revisar]);

  // Cada globo se retira solo cuando cumple su tiempo.
  useEffect(() => {
    if (visibles.length === 0 || segundos === 0) return;
    const t = setTimeout(() => setVisibles((v) => v.slice(1)), segundos * 1000);
    return () => clearTimeout(t);
  }, [visibles, segundos]);

  const enElTablero = location.pathname.startsWith('/domicilios');
  if (!puedeVerDomicilios || enElTablero || visibles.length === 0) return null;

  return (
    <div
      className={`pointer-events-none fixed z-[70] flex w-[22rem] max-w-[calc(100vw-2rem)] flex-col gap-2 ${clasesDePosicion(posicion)}`}
      role="status"
      aria-live="polite"
    >
      {visibles.slice(-3).map((a) => (
        <button
          key={a.clave}
          onClick={() => {
            setVisibles((v) => v.filter((x) => x.clave !== a.clave));
            navigate('/domicilios');
          }}
          className={`pointer-events-auto flex items-start gap-3 rounded-xl border-2 p-3 text-left shadow-lg transition hover:brightness-95 ${ACENTO[a.tipo]}`}
        >
          <span className="mt-0.5 shrink-0">{ICONO[a.tipo]}</span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-bold">{a.titulo}</span>
            <span className="block text-xs opacity-90">{a.detalle}</span>
            <span className="mt-1 block text-xs font-semibold uppercase tracking-wide opacity-90">
              Toca para verlo
            </span>
          </span>
          <span
            onClick={(e) => {
              e.stopPropagation();
              setVisibles((v) => v.filter((x) => x.clave !== a.clave));
            }}
            aria-label="Cerrar aviso"
            className="shrink-0 rounded p-1 opacity-60 hover:opacity-100"
          >
            <X className="h-4 w-4" />
          </span>
        </button>
      ))}
    </div>
  );
}

/** Cada tipo con su color: se distinguen de reojo, sin leer. */
const ACENTO: Record<TipoAviso, string> = {
  llego_del_enlace: 'border-blue-300 bg-blue-50 text-blue-900',
  falta_cotizar: 'border-amber-300 bg-amber-50 text-amber-900',
  aviso_pago: 'border-emerald-300 bg-emerald-50 text-emerald-900',
  cliente_recibio: 'border-slate-300 bg-white text-slate-800',
};

const ICONO: Record<TipoAviso, JSX.Element> = {
  llego_del_enlace: <Link2 className="h-5 w-5" />,
  falta_cotizar: <Bike className="h-5 w-5" />,
  aviso_pago: <CreditCard className="h-5 w-5" />,
  cliente_recibio: <PackageCheck className="h-5 w-5" />,
};
